import { useCallback, useEffect, useState } from 'react'
import { ErroreApi } from '../../servizi/api.ts'
import { useSessione } from '../../servizi/sessione.tsx'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import {
  AreaTesto, Bottone, BottoneIcona, Campo, Etichetta, Linguette, Modale, Scelta, Spunta, Tabella,
} from '../../componenti/base.tsx'
import { IconaChiudi } from '../../componenti/Icone.tsx'
import { COLORI, type CasellaWebmail, type Colore } from '../../servizi/posta.ts'
import { Bandierina } from './Segni.tsx'
import * as arrivo from '../../servizi/posta-arrivo.ts'
import '../../stile-posta-arrivo.css'

/**
 * Risposta di assenza e regole di smistamento di una casella (540, richiesta di Davide del 2/11/2026: le
 * funzioni di Roundcube a cui CIDIS era abituata).
 *
 * Il punto d'ingresso sta nella colonna delle cartelle della casella aperta, per chi ne ha l'accesso
 * pieno: il comando «Assenza e regole» e, a risposta accesa, la riga che lo dice. Il comando apre una
 * finestra con due linguette:
 *  - «Risposta di assenza»: interruttore, periodo, oggetto facoltativo, testo, e le ultime risposte (a chi
 *    e quando), perche' le risposte non vanno in «Inviata»;
 *  - «Regole»: le regole in ordine, accese e spente una per una, su e giu', modifica ed eliminazione, e
 *    «Applica ora alla Posta in arrivo» per i messaggi gia' arrivati.
 * Le regole le applica l'API all'arrivo (servizi/posta-arrivo-regole.ts): qui si scrivono soltanto.
 */

const testoErrore = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

const giorno = (v: string) => new Date(`${v}T12:00:00`).toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })
const quando = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

/** Oggi nel calendario italiano, AAAA-MM-GG: il valore del campo data. */
const oggi = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' })

function statoAssenza(a: Pick<arrivo.RispostaAssenza, 'attiva' | 'in_corso' | 'dal' | 'al'>): {
  testo: string; tono: 'positivo' | 'neutro' | 'attenzione'
} {
  if (!a.attiva) return { testo: 'spenta', tono: 'neutro' }
  if (a.in_corso) return { testo: a.al ? `accesa fino al ${giorno(a.al)}` : 'accesa', tono: 'positivo' }
  if (a.dal > oggi()) return { testo: `accesa dal ${giorno(a.dal)}`, tono: 'attenzione' }
  return { testo: `terminata il ${giorno(a.al ?? a.dal)}`, tono: 'neutro' }
}

/**
 * Il punto d'ingresso nella colonna delle cartelle. `applicate`: dopo «Applica ora», per ricaricare
 * cartelle ed elenco della pagina.
 */
export function AssenzaRegole({ casella, applicate }: { casella: CasellaWebmail; applicate?: () => void }) {
  const [aperta, setAperta] = useState(false)
  const [stato, setStato] = useState<arrivo.RispostaAssenza | null>(null)

  const carica = useCallback(() => {
    arrivo.leggiAssenza(casella.id).then(setStato).catch(() => setStato(null))
  }, [casella.id])
  useEffect(() => { setStato(null); carica() }, [carica])

  const riga = stato?.attiva ? statoAssenza(stato) : null
  return (
    <div className="arrivo-ingresso">
      {riga && (
        <p className="posta-nota arrivo-stato">
          Risposta di assenza <Etichetta tono={riga.tono}>{riga.testo}</Etichetta>
        </p>
      )}
      <Bottone variante="secondario" dimensione="piccolo" onClick={() => setAperta(true)}>
        Assenza e regole
      </Bottone>
      {aperta && (
        <FinestraAssenzaRegole casella={casella}
          chiudi={() => { setAperta(false); carica() }}
          applicate={applicate} />
      )}
    </div>
  )
}

function FinestraAssenzaRegole({ casella, chiudi, applicate }: {
  casella: CasellaWebmail; chiudi: () => void; applicate?: () => void
}) {
  const [linguetta, setLinguetta] = useState<'assenza' | 'regole'>('assenza')
  return (
    <Modale titolo={`Assenza e regole di ${casella.indirizzo}`} chiudi={chiudi} larga>
      <Linguette etichetta="Risposta di assenza e regole" attiva={linguetta} cambia={setLinguetta}
        voci={[{ valore: 'assenza', etichetta: 'Risposta di assenza' }, { valore: 'regole', etichetta: 'Regole' }]} />
      <div className="arrivo-pannello">
        {linguetta === 'assenza' ? <PannelloAssenza casella={casella} /> : <PannelloRegole casella={casella} applicate={applicate} />}
      </div>
    </Modale>
  )
}

// --- risposta di assenza ----------------------------------------------------------------------------

const ESITI: Record<arrivo.StatoRisposta, string> = {
  in_coda: 'in partenza', inviata: 'inviata', errore: 'non partita', annullata: 'non partita',
}

function PannelloAssenza({ casella }: { casella: CasellaWebmail }) {
  const { puo } = useSessione()
  // «Segna anche nell'agenda» (559, decisione 5): solo sulla propria casella e per chi ha un'agenda.
  const conAgenda = casella.ambito === 'utente' && casella.mia && (puo('agenda.leggi') || puo('mediazione.propria') || puo('agenda.propria'))
  const [segna, setSegna] = useState(false)
  const [dati, setDati] = useState<arrivo.RispostaAssenza | null>(null)
  const [modulo, setModulo] = useState<arrivo.DatiAssenza | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [fatto, setFatto] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  const carica = useCallback(() => {
    arrivo.leggiAssenza(casella.id).then((a) => {
      setDati(a)
      setModulo({ attiva: a.attiva, oggetto: a.oggetto, testo: a.testo, dal: a.dal, al: a.al })
    }).catch((e) => setErrore(testoErrore(e)))
  }, [casella.id])
  useEffect(() => { carica() }, [carica])

  async function salva() {
    if (!modulo) return
    setInCorso(true); setErrore(null); setFatto(null)
    try {
      const r = await arrivo.salvaAssenza(casella.id, {
        ...modulo, al: modulo.al || null, oggetto: modulo.oggetto || null, ...(conAgenda && segna ? { segna_in_agenda: true } : {}),
      })
      setFatto([!r.attiva ? 'Risposta di assenza spenta.'
        : r.in_corso ? 'Risposta di assenza accesa: da adesso risponde ai messaggi che arrivano.'
          : `Risposta di assenza salvata: risponde dal ${giorno(r.dal)}.`,
      r.indisponibilita_id ? 'Nell’agenda risulti non disponibile negli stessi giorni.' : ''].filter(Boolean).join(' '))
      setSegna(false)
      carica()
    } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }

  if (!dati || !modulo) return errore ? <Avviso tipo="errore">{errore}</Avviso> : <Attesa />
  const cambia = (p: Partial<arrivo.DatiAssenza>) => { setModulo({ ...modulo, ...p }); setFatto(null) }
  const s = statoAssenza(dati)

  return (
    <>
      <p className="arrivo-intro">
        Risponde da solo a chi scrive alla casella mentre sei via. Stato: <Etichetta tono={s.tono}>{s.testo}</Etichetta>
      </p>
      {dati.solo_interni && (
        <Avviso>Questa casella scrive solo dentro CIDIS: la risposta parte solo ai colleghi.</Avviso>
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {fatto && <Avviso tipo="successo">{fatto}</Avviso>}
      <Spunta etichetta="Risposta di assenza accesa" valore={modulo.attiva} cambia={(v) => cambia({ attiva: v })} />
      <div className="arrivo-date">
        <Campo tipo="date" etichetta="Dal giorno" obbligatorio valore={modulo.dal} cambia={(v) => cambia({ dal: v })} />
        <Campo tipo="date" etichetta="Al giorno" valore={modulo.al ?? ''} min={modulo.dal}
               aiuto="Facoltativo: senza, risponde finché non la spegni." cambia={(v) => cambia({ al: v || null })} />
      </div>
      {conAgenda && modulo.attiva && (
        <Spunta etichetta="Segna anche nell’agenda" valore={segna} cambia={setSegna}
                aiuto="Negli stessi giorni risulti «non disponibile» nell’agenda, senza motivo: lo vede chi programma i turni. Serve l’ultimo giorno." />
      )}
      <Campo etichetta="Oggetto" valore={modulo.oggetto ?? ''} maxLength={200}
             segnaposto="Risposta automatica: oggetto del messaggio ricevuto"
             aiuto="Facoltativo: senza, «Risposta automatica:» e l’oggetto del messaggio ricevuto."
             cambia={(v) => cambia({ oggetto: v || null })} />
      <AreaTesto etichetta="Testo" righe={6} maxLength={5000} valore={modulo.testo} obbligatorio={modulo.attiva}
                 segnaposto="Sono fuori ufficio fino al … Per urgenze scrivete a …"
                 cambia={(v) => cambia({ testo: v })} />
      <p className="nota-modulo">
        A ogni mittente risponde una volta ogni {dati.giorni_fra_risposte} giorni. Non risponde a liste e newsletter,
        a messaggi automatici, alla posta indesiderata, ai mittenti non verificati, alle altre caselle che usi e a
        chi ti ha messo in copia nascosta. La risposta non va in «Inviata»: le ultime sono qui sotto.
      </p>
      <div className="arrivo-comandi">
        <Bottone variante="primario" inCorso={inCorso} onClick={() => void salva()}>Salva</Bottone>
      </div>

      <h3 className="arrivo-titoletto">Ultime risposte</h3>
      <Tabella intestazioni={['A', 'Quando', 'Esito']}
               vuoto={<Vuoto testo="Nessuna risposta negli ultimi 30 giorni." />}>
        {dati.risposte.map((r) => (
          <tr key={r.id}>
            <td className="arrivo-indirizzo">{r.destinatario}</td>
            <td>{quando.format(new Date(r.inviata_il ?? r.creata_il))}</td>
            <td>
              <Etichetta tono={r.stato === 'inviata' ? 'positivo' : r.stato === 'in_coda' ? 'neutro' : 'attenzione'}>
                {ESITI[r.stato]}
              </Etichetta>
              {r.errore && <span className="piccolo tenue"> {r.errore}</span>}
            </td>
          </tr>
        ))}
      </Tabella>
    </>
  )
}

// --- regole -----------------------------------------------------------------------------------------

const CONDIZIONE_VUOTA: arrivo.Condizione = { campo: 'mittente', operatore: 'contiene', valore: '' }

function PannelloRegole({ casella, applicate }: { casella: CasellaWebmail; applicate?: () => void }) {
  const [regole, setRegole] = useState<arrivo.Regola[] | null>(null)
  const [cartelle, setCartelle] = useState<arrivo.CartellaDestinazione[]>([])
  const [etichette, setEtichette] = useState<arrivo.EtichettaRegola[]>([])
  const [errore, setErrore] = useState<string | null>(null)
  const [fatto, setFatto] = useState<string | null>(null)
  const [scrivi, setScrivi] = useState<arrivo.Regola | 'nuova' | null>(null)
  const [daEliminare, setDaEliminare] = useState<string | null>(null)
  const [applica, setApplica] = useState(false)

  const carica = useCallback(() => {
    arrivo.leggiRegole(casella.id).then((r) => { setRegole(r.regole); setCartelle(r.cartelle); setEtichette(r.etichette) })
      .catch((e) => setErrore(testoErrore(e)))
  }, [casella.id])
  useEffect(() => { carica() }, [carica])

  async function esegui(azione: () => Promise<unknown>, messaggio?: string) {
    setErrore(null); setFatto(null)
    try {
      await azione()
      if (messaggio) setFatto(messaggio)
      carica()
    } catch (e) { setErrore(testoErrore(e)) }
  }

  async function applicaOra() {
    setApplica(true); setErrore(null); setFatto(null)
    try {
      const r = await arrivo.applicaOra(casella.id)
      const parti = [
        r.esaminati === 1 ? '1 messaggio guardato' : `${r.esaminati} messaggi guardati`,
        r.spostati === 1 ? '1 spostato' : `${r.spostati} spostati`,
        r.letti === 1 ? '1 segnato come letto' : `${r.letti} segnati come letti`,
        ...(r.bandierine ? [r.bandierine === 1 ? '1 conversazione con la bandierina' : `${r.bandierine} conversazioni con la bandierina`] : []),
        ...(r.etichettate ? [r.etichettate === 1 ? '1 etichetta messa' : `${r.etichettate} etichette messe`] : []),
      ]
      setFatto(`Regole applicate alla Posta in arrivo: ${parti.join(', ')}.`
        + (r.oltre ? ' La Posta in arrivo ha più messaggi: sono stati guardati i 5.000 più recenti.' : ''))
      applicate?.()
    } catch (e) { setErrore(testoErrore(e)) } finally { setApplica(false) }
  }

  if (!regole) return errore ? <Avviso tipo="errore">{errore}</Avviso> : <Attesa />
  if (scrivi) {
    return (
      <EditorRegola casella={casella} cartelle={cartelle} etichette={etichette} regola={scrivi === 'nuova' ? null : scrivi}
        chiudi={(salvata) => { setScrivi(null); if (salvata) { setFatto(salvata); carica() } }} />
    )
  }

  return (
    <>
      <p className="arrivo-intro">
        Le regole smistano i messaggi che arrivano nella Posta in arrivo, dalla prima all’ultima: spostano in una
        cartella, segnano come letto, mettono la bandierina o un’etichetta per tutti, fermano le successive. Non
        toccano la posta indesiderata.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {fatto && <Avviso tipo="successo">{fatto}</Avviso>}
      {regole.length === 0 && <Vuoto testo="Nessuna regola per questa casella." />}
      <ol className="arrivo-regole">
        {regole.map((r, i) => {
          const d = arrivo.descriviRegola(r, cartelle, etichette)
          const colore = r.azioni.find((a): a is Extract<arrivo.Azione, { tipo: 'bandierina' }> => a.tipo === 'bandierina')?.colore
          return (
            <li key={r.id} className={`arrivo-regola${r.attiva ? '' : ' spenta'}`}>
              <div className="arrivo-regola-testo">
                <span className="arrivo-regola-numero">{i + 1}</span>
                <div>
                  <p className="arrivo-regola-se">{d.se}</p>
                  <p className="arrivo-regola-allora">
                    → {d.allora}{colore && <> <Bandierina colore={colore} per="tutti" /></>}
                  </p>
                  {!r.attiva && !r.avviso && <Etichetta>spenta</Etichetta>}
                </div>
              </div>
              {r.avviso && (
                <Avviso tipo="attenzione">
                  {r.avviso === 'cartella_eliminata' ? 'La cartella' : 'L’etichetta'} di questa regola non c’è più: la
                  regola è spenta. Premi «Modifica» e scegline un’altra.
                </Avviso>
              )}
              <div className="arrivo-regola-comandi">
                <Spunta etichetta="Accesa" valore={r.attiva} disabilitato={!!r.avviso}
                        cambia={(v) => void esegui(() => arrivo.accendiRegola(r.id, v))} />
                <Bottone variante="discreto" dimensione="piccolo" disabled={i === 0} aria-label={`Sposta su la regola ${i + 1}`}
                         onClick={() => void esegui(() => arrivo.spostaRegola(r.id, 'su'))}>Su</Bottone>
                <Bottone variante="discreto" dimensione="piccolo" disabled={i === regole.length - 1}
                         aria-label={`Sposta giù la regola ${i + 1}`}
                         onClick={() => void esegui(() => arrivo.spostaRegola(r.id, 'giu'))}>Giù</Bottone>
                <Bottone variante="discreto" dimensione="piccolo" onClick={() => { setFatto(null); setScrivi(r) }}>Modifica</Bottone>
                {daEliminare === r.id
                  ? (
                    <>
                      <Bottone variante="critico" dimensione="piccolo"
                               onClick={() => { setDaEliminare(null); void esegui(() => arrivo.eliminaRegola(r.id), 'Regola eliminata.') }}>
                        Elimina davvero
                      </Bottone>
                      <Bottone variante="discreto" dimensione="piccolo" onClick={() => setDaEliminare(null)}>Annulla</Bottone>
                    </>
                  )
                  : <Bottone variante="discreto" dimensione="piccolo" onClick={() => setDaEliminare(r.id)}>Elimina</Bottone>}
              </div>
            </li>
          )
        })}
      </ol>
      <div className="arrivo-comandi">
        <Bottone variante="primario" onClick={() => { setFatto(null); setScrivi('nuova') }}>Nuova regola</Bottone>
        <Bottone variante="secondario" inCorso={applica} disabled={!regole.some((r) => r.attiva)}
                 title="Le regole accese, sui messaggi già nella Posta in arrivo"
                 onClick={() => void applicaOra()}>
          Applica ora alla Posta in arrivo
        </Bottone>
      </div>
    </>
  )
}

function EditorRegola({ casella, cartelle, etichette, regola, chiudi }: {
  casella: CasellaWebmail; cartelle: arrivo.CartellaDestinazione[]; etichette: arrivo.EtichettaRegola[]
  regola: arrivo.Regola | null; chiudi: (salvata: string | null) => void
}) {
  const sposta = regola?.azioni.find((a): a is Extract<arrivo.Azione, { tipo: 'sposta' }> => a.tipo === 'sposta')
  const cartellaValida = sposta && cartelle.some((c) => c.id === sposta.cartella_id) ? sposta.cartella_id : ''
  const [combinazione, setCombinazione] = useState<arrivo.Regola['combinazione']>(regola?.combinazione ?? 'tutte')
  const [condizioni, setCondizioni] = useState<arrivo.Condizione[]>(regola?.condizioni ?? [{ ...CONDIZIONE_VUOTA }])
  const [spostaIn, setSpostaIn] = useState(!!sposta)
  // La cartella eliminata non si sostituisce in silenzio: «Scegli…», e la regola non si salva senza.
  const [cartella, setCartella] = useState(cartellaValida || (sposta ? '' : cartelle[0]?.id ?? ''))
  const [letta, setLetta] = useState(!!regola?.azioni.some((a) => a.tipo === 'segna_letta'))
  const bandierina = regola?.azioni.find((a): a is Extract<arrivo.Azione, { tipo: 'bandierina' }> => a.tipo === 'bandierina')
  const [conBandierina, setConBandierina] = useState(!!bandierina)
  const [colore, setColore] = useState<Colore>(bandierina?.colore ?? 'rosso')
  const etichetta = regola?.azioni.find((a): a is Extract<arrivo.Azione, { tipo: 'etichetta' }> => a.tipo === 'etichetta')
  const [conEtichetta, setConEtichetta] = useState(!!etichetta)
  // L'etichetta eliminata, come la cartella, non si sostituisce in silenzio.
  const [etichettaId, setEtichettaId] = useState(
    etichetta && etichette.some((e) => e.id === etichetta.etichetta_id) ? etichetta.etichetta_id
      : etichetta ? '' : etichette[0]?.id ?? '')
  const [ferma, setFerma] = useState(!!regola?.azioni.some((a) => a.tipo === 'ferma'))
  const [attiva, setAttiva] = useState(regola ? regola.attiva || !!regola.avviso : true)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  const cambiaCondizione = (i: number, p: Partial<arrivo.Condizione>) =>
    setCondizioni((c) => c.map((x, j) => (j === i ? { ...x, ...p } : x)))

  const azioni: arrivo.Azione[] = [
    ...(spostaIn && cartella ? [{ tipo: 'sposta' as const, cartella_id: cartella }] : []),
    ...(letta ? [{ tipo: 'segna_letta' as const }] : []),
    ...(conBandierina ? [{ tipo: 'bandierina' as const, colore }] : []),
    ...(conEtichetta && etichettaId ? [{ tipo: 'etichetta' as const, etichetta_id: etichettaId }] : []),
    ...(ferma ? [{ tipo: 'ferma' as const }] : []),
  ]
  const completa = condizioni.every((c) => c.valore.trim()) && azioni.length > 0 && (!spostaIn || !!cartella)
    && (!conEtichetta || !!etichettaId)

  async function salva() {
    setInCorso(true); setErrore(null)
    try {
      const dati: arrivo.DatiRegola = {
        attiva, combinazione, azioni, condizioni: condizioni.map((c) => ({ ...c, valore: c.valore.trim() })),
      }
      if (regola) await arrivo.modificaRegola(regola.id, dati)
      else await arrivo.creaRegola(casella.id, dati)
      chiudi(regola ? 'Regola salvata.' : 'Regola aggiunta in fondo all’elenco.')
    } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }

  return (
    <div className="arrivo-editor">
      <h3 className="arrivo-titoletto">{regola ? 'Modifica la regola' : 'Nuova regola'}</h3>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <Scelta etichetta="Quando" valore={combinazione} cambia={(v) => setCombinazione(v as arrivo.Regola['combinazione'])}
              opzioni={[
                { valore: 'tutte', etichetta: 'il messaggio rispetta tutte le condizioni' },
                { valore: 'almeno_una', etichetta: 'il messaggio rispetta almeno una condizione' },
              ]} />
      <fieldset className="arrivo-condizioni">
        <legend>Condizioni</legend>
        {condizioni.map((c, i) => (
          <div key={i} className="arrivo-condizione">
            <Scelta etichetta="Campo" valore={c.campo} cambia={(v) => cambiaCondizione(i, { campo: v as arrivo.Campo })}
                    opzioni={(Object.keys(arrivo.NOMI_CAMPI) as arrivo.Campo[]).map((k) => ({ valore: k, etichetta: arrivo.NOMI_CAMPI[k] }))} />
            <Scelta etichetta="Confronto" valore={c.operatore} cambia={(v) => cambiaCondizione(i, { operatore: v as arrivo.Operatore })}
                    opzioni={(Object.keys(arrivo.NOMI_OPERATORI) as arrivo.Operatore[]).map((k) => ({ valore: k, etichetta: arrivo.NOMI_OPERATORI[k] }))} />
            <Campo etichetta="Testo" valore={c.valore} maxLength={200} cambia={(v) => cambiaCondizione(i, { valore: v })}
                   segnaposto={c.campo === 'oggetto' ? 'fattura' : 'nome o indirizzo, anche una parte'} />
            <div className="arrivo-condizione-togli">
              <BottoneIcona icona={<IconaChiudi />} etichetta={`Togli la condizione ${i + 1}`} disabled={condizioni.length === 1}
                            onClick={() => setCondizioni((x) => x.filter((_, j) => j !== i))} />
            </div>
          </div>
        ))}
        {condizioni.length < 10 && (
          <Bottone variante="discreto" dimensione="piccolo" onClick={() => setCondizioni((x) => [...x, { ...CONDIZIONE_VUOTA }])}>
            Aggiungi una condizione
          </Bottone>
        )}
        <p className="nota-modulo">
          Maiuscole e minuscole non contano. «è» vuole il testo intero: l’oggetto «Fattura» non è «R: Fattura». Il
          mittente si confronta con nome e indirizzo, il destinatario con gli indirizzi in A e Cc.
        </p>
      </fieldset>
      <fieldset className="arrivo-azioni">
        <legend>Azioni</legend>
        <div className="arrivo-sposta">
          <Spunta etichetta="Sposta nella cartella" valore={spostaIn} cambia={setSpostaIn} disabilitato={!cartelle.length} />
          {spostaIn && (
            <Scelta etichetta="Cartella" valore={cartella} cambia={setCartella}
                    opzioni={cartelle.map((c) => ({ valore: c.id, etichetta: c.nome }))} vuota={cartella ? undefined : 'Scegli…'} />
          )}
        </div>
        <Spunta etichetta="Segna come letta" valore={letta} cambia={setLetta} />
        <div className="arrivo-sposta">
          <Spunta etichetta="Metti la bandierina" valore={conBandierina} cambia={setConBandierina}
                  aiuto="Per tutti quelli che usano la casella." />
          {conBandierina && (
            <div className="arrivo-colore">
              <Scelta etichetta="Bandierina" valore={colore} cambia={(v) => setColore(v as Colore)}
                      opzioni={COLORI.map((c) => ({ valore: c.valore, etichetta: c.nome }))} />
              <Bandierina colore={colore} per="tutti" />
            </div>
          )}
        </div>
        <div className="arrivo-sposta">
          <Spunta etichetta="Metti l’etichetta" valore={conEtichetta} cambia={setConEtichetta} disabilitato={!etichette.length}
                  aiuto={etichette.length ? 'Fra le etichette per tutti della casella.'
                    : 'La casella non ha etichette per tutti: creane una dal menu «Bandierina ed etichette» di una conversazione.'} />
          {conEtichetta && (
            <Scelta etichetta="Etichetta" valore={etichettaId} cambia={setEtichettaId}
                    opzioni={etichette.map((e) => ({ valore: e.id, etichetta: e.nome }))} vuota={etichettaId ? undefined : 'Scegli…'} />
          )}
        </div>
        <Spunta etichetta="Ferma le regole successive" valore={ferma} cambia={setFerma}
                aiuto="Dopo questa regola il messaggio non passa alle altre." />
      </fieldset>
      <Spunta etichetta="Regola accesa" valore={attiva} cambia={setAttiva} />
      <div className="arrivo-comandi">
        <Bottone variante="discreto" onClick={() => chiudi(null)}>Annulla</Bottone>
        <Bottone variante="primario" inCorso={inCorso} disabled={!completa} onClick={() => void salva()}>Salva la regola</Bottone>
      </div>
    </div>
  )
}
